import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import { addCatalogItem, removeCatalogItem, isInCatalog, type CatalogStorageItem } from '@/lib/catalogStorage';
import { broadcastTooltipShow, onTooltipShow } from '@/components/base/Tooltip/Tooltip';
import XBoldIcon from '@/icons/XBold.svg?react';
import QuizInfoIcon from '@/icons/quiz-info.svg?react';
import CheckboxCheckedIcon from '@/icons/CheckboxChecked.svg?react';
import CheckboxUncheckedIcon from '@/icons/CheckboxUnchecked.svg?react';
import IconRight from '@/icons/IconRight.svg?react';
import GoToExternalSiteIcon from '@/icons/GoToExternalSite.svg?react';
import BookmarkIcon from '@/icons/Bookmark.svg?react';

/**
 * Tooltip data for theme info icons
 */
interface ThemeTooltipData {
  heading: string;
  items: string[];
}

/**
 * QuizThemeTooltipPortal - Renders tooltip for theme info icons
 * Matches the styling of ResourceCardTooltipPortal from BrowseResourcesSection
 */
function QuizThemeTooltipPortal({
  tooltip,
  triggerRef,
  isMobile,
  isTablet,
}: {
  tooltip: ThemeTooltipData;
  triggerRef: React.RefObject<HTMLElement | null>;
  isMobile?: boolean;
  isTablet?: boolean;
}) {
  // triggerRect holds the icon's bounding rect for tablet/desktop positioning
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  // finalStyle holds the fully resolved position after measuring the rendered tooltip
  const [finalStyle, setFinalStyle] = useState<React.CSSProperties | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Phase 1: Capture the trigger's bounding rect before paint
  useLayoutEffect(() => {
    if (triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
      // Reset final style when trigger changes so we re-measure
      setFinalStyle(null);
    }
  }, [triggerRef]);

  // Phase 2: After the tooltip renders, measure its height and determine
  // whether to open downward (default) or upward (flip).
  // Mobile: horizontally centered, positioned above/below trigger.
  // Tablet/Desktop: right-anchored to icon's right edge, clamped to left viewport edge.
  useLayoutEffect(() => {
    if (!triggerRect || !tooltipRef.current) return;

    const tooltipEl = tooltipRef.current;
    const tooltipHeight = tooltipEl.offsetHeight;
    const gap = 8; // gap between icon and tooltip
    const viewportHeight = window.innerHeight;

    if (isMobile) {
      // Mobile: open below if trigger is in top half, above if in bottom half
      const triggerMidpoint = (triggerRect.top + triggerRect.bottom) / 2;
      const inTopHalf = triggerMidpoint < viewportHeight / 2;

      const verticalStyle: React.CSSProperties = inTopHalf
        ? { top: triggerRect.bottom + gap }
        : { bottom: viewportHeight - triggerRect.top + gap };

      setFinalStyle({
        ...verticalStyle,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100vw - 88px)',
        maxWidth: '340px',
      });
    } else {
      // Tablet/Desktop: anchor from top-left of icon, same width as mobile
      const spaceBelow = viewportHeight - triggerRect.bottom - gap;
      const spaceAbove = triggerRect.top - gap;

      const verticalStyle: React.CSSProperties =
        spaceBelow >= tooltipHeight || spaceBelow >= spaceAbove
          ? { top: triggerRect.bottom + gap }
          : { bottom: viewportHeight - triggerRect.top + gap };

      // Tablet: right-anchor so tooltip opens toward the left
      // Desktop: left-anchor so tooltip opens toward the right
      const viewportWidth = window.innerWidth;
      const horizontalStyle: React.CSSProperties = isTablet
        ? { right: viewportWidth - triggerRect.right }
        : { left: triggerRect.left };

      setFinalStyle({
        ...verticalStyle,
        ...horizontalStyle,
        width: 'calc(100vw - 88px)',
        maxWidth: '340px',
      });
    }
  }, [triggerRect, isMobile, isTablet]);

  // Don't render until we have trigger position (phase 1)
  if (!triggerRect) {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className={cn(
        'fixed z-[200]',
        'bg-white',
        'border border-[#D9D9D9]',
        'rounded-[4px]',
        'py-3 px-4',
        'pointer-events-none'
      )}
      style={{
        ...(finalStyle ?? {
          // While measuring (phase 1→2 gap): render far off-screen so we can measure
          // height without a visible flash. Positioned off-screen with pointer-events-none.
          top: -9999,
          left: isMobile ? '50%' : isTablet ? undefined : triggerRect.left,
          right: isMobile ? undefined : isTablet ? window.innerWidth - triggerRect.right : undefined,
          transform: isMobile ? 'translateX(-50%)' : undefined,
          width: 'calc(100vw - 88px)',
          maxWidth: '340px',
        }),
        boxShadow: '0px 4px 4px 0px #A5A5A540',
      }}
    >
      <p
        className={cn('text-black m-0', tooltip.items.length > 0 && 'mb-2')}
        style={{
          fontFamily: "'Source Sans Pro', sans-serif",
          fontWeight: tooltip.items.length > 0 ? 600 : 400,
          fontSize: '20px',
          lineHeight: '24px',
        }}
      >
        {tooltip.heading}
      </p>
      {tooltip.items.length > 0 && (
        <ul className="m-0 pl-5 list-disc">
          {tooltip.items.map((item, index) => (
            <li
              key={index}
              className="text-black"
              style={{
                fontFamily: "'Source Sans Pro', sans-serif",
                fontWeight: 400,
                fontSize: '20px',
                lineHeight: '24px',
              }}
            >
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body
  );
}

/**
 * Resource data for quiz results
 */
export interface QuizResourceData {
  id: string;
  type: 'WEBSITE' | 'TOOL' | 'DOCUMENT';
  title: string;
  source: string;
  description: string;
  href: string;
}

/**
 * Topic/subtheme data within a theme
 */
export interface QuizTopicData {
  id: string;
  title: string;
  subthemeQuizOption: string;
  resources?: QuizResourceData[];
}

/**
 * Theme data for quiz options
 */
export interface QuizThemeData {
  id: string;
  olderAdultThemeQuizOption: string;
  caregiverThemeQuizOption: string;
  topics?: QuizTopicData[];
}

/**
 * Service result banner content (for services path)
 */
export interface ServiceResultBannerContent {
  title: string;
  description: string;
  buttonLabel: string;
  href: string;
}

/**
 * Link item for events near-me results
 */
export interface EventLinkItem {
  label: string;
  href: string;
}

/**
 * Events near-me result content (list of links instead of banner)
 */
export interface EventsNearMeContent {
  title: string;
  links: EventLinkItem[];
}

/**
 * Content interface for ResourceFinderQuizModal
 */
export interface ResourceFinderQuizModalContent {
  /** Modal title for accessibility */
  title: string;
  /** Close button label for accessibility */
  closeLabel: string;
  /** Step 1 content */
  step1?: {
    title: string;
    subtitle: string;
    question: string;
    options: Array<{
      id: string;
      label: string;
      value: 'informational' | 'services' | 'events';
      tooltip?: string;
    }>;
  };
  /** Step 2 content for informational resources path */
  step2?: {
    question: string;
    options: Array<{
      id: string;
      label: string;
      value: 'older-adult' | 'caregiver';
    }>;
  };
  /** Step 2 content for services path */
  step2Services?: {
    question: string;
    options: Array<{
      id: string;
      label: string;
      value: 'facility-based' | 'healthy-aging';
    }>;
  };
  /** Step 2 content for events path */
  step2Events?: {
    question: string;
    options: Array<{
      id: string;
      label: string;
      value: 'virtual' | 'near-me';
    }>;
  };
  /** Step 3 content - question text only, options derived from themes */
  step3?: {
    question: string;
  };
  /** Step 4 content - question text only, options derived from selected theme's topics */
  step4?: {
    question: string;
    /** Label for the "Select all that apply" button */
    selectAllLabel?: string;
  };
  /** Step 5 content - results display */
  step5?: {
    title: string;
    viewAllLabel: string;
    saveToCatalogLabel: string;
    savedToCatalogLabel: string;
    restartLabel: string;
    completeLabel: string;
    /** Feedback banner content */
    feedbackBanner?: {
      title: string;
      subtitle: string;
      buttonLabel: string;
    };
    /** Service result banners for services path */
    servicesBanners?: {
      facilityBased: ServiceResultBannerContent;
      healthyAging: ServiceResultBannerContent;
    };
    /** Event result banners for events path */
    eventsBanners?: {
      virtual: ServiceResultBannerContent;
      nearMe: EventsNearMeContent;
    };
  };
  /** Navigation button labels */
  buttons?: {
    next: string;
    back: string;
  };
}

/**
 * Props interface for ResourceFinderQuizModal component
 */
export interface ResourceFinderQuizModalProps {
  /** Modal content */
  content?: ResourceFinderQuizModalContent;
  /** Theme data for generating step 3 options */
  themes?: QuizThemeData[];
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed, includes whether resources were saved */
  onClose: (hadSavedResources?: boolean) => void;
  /** Callback when catalog changes (resource saved/unsaved) */
  onCatalogChange?: () => void;
  /** Initial resource type selection from quiz banner */
  initialResourceType?: 'informational' | 'services' | 'events';
  /** Additional CSS classes */
  className?: string;
}

const TOTAL_STEPS = 5;

/**
 * Custom Radio Button component for the quiz
 */
interface QuizRadioButtonProps {
  id: string;
  name: string;
  label: string;
  checked: boolean;
  onChange: () => void;
  /** Use bodyMedium typography (for steps 2 and 3) */
  useMediumText?: boolean;
  /** Show info icon after label (for step 3) */
  showInfoIcon?: boolean;
  /** Tooltip data for info icon hover */
  tooltipData?: ThemeTooltipData;
  /** Whether to use tablet touch-friendly styling */
  isTablet?: boolean;
  /** Whether viewport is mobile (< 800px) for tooltip positioning */
  isMobile?: boolean;
}

// Unique singleton ID counter for quiz radio button tooltips
let quizRadioTooltipIdCounter = 0;

function QuizRadioButton({ id, name, label, checked, onChange, useMediumText, showInfoIcon, tooltipData, isTablet, isMobile }: QuizRadioButtonProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const iconRef = useRef<HTMLSpanElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const instanceIdRef = useRef<string>('');
  // Track whether tooltip was opened by click (sticky — mouseLeave won't dismiss)
  const clickLockedRef = useRef(false);
  // Briefly suppress hover re-open after a click-dismiss (mouse may still be over icon)
  const clickDismissedRef = useRef(false);

  // Assign unique ID for singleton coordination
  useEffect(() => {
    quizRadioTooltipIdCounter += 1;
    instanceIdRef.current = `quiz-radio-tooltip-${quizRadioTooltipIdCounter}`;
  }, []);

  // Singleton: listen for other tooltips showing and hide ours
  useEffect(() => {
    return onTooltipShow((activeId) => {
      if (activeId !== instanceIdRef.current) {
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
        clickLockedRef.current = false;
        clickDismissedRef.current = false;
        setIsTooltipVisible(false);
      }
    });
  }, []);

  const handleIconMouseEnter = useCallback(() => {
    // Don't interfere if tooltip is click-locked or just click-dismissed
    if (clickLockedRef.current || clickDismissedRef.current) return;
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsTooltipVisible(true);
    // Broadcast to global singleton — hides all other tooltips
    broadcastTooltipShow(instanceIdRef.current);
  }, []);

  const handleIconMouseLeave = useCallback(() => {
    // Reset click-dismissed flag so hover works normally on next mouseenter
    clickDismissedRef.current = false;
    // Don't dismiss if tooltip was opened by click
    if (clickLockedRef.current) return;
    hideTimeoutRef.current = setTimeout(() => {
      setIsTooltipVisible(false);
    }, 100);
  }, []);

  const handleIconClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // On desktop, hover handles tooltips — click is a no-op
    if (!isTablet && !isMobile) return;
    // Tablet/mobile: tap to toggle with click-lock
    if (clickLockedRef.current) {
      clickLockedRef.current = false;
      clickDismissedRef.current = true;
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setIsTooltipVisible(false);
    } else {
      clickLockedRef.current = true;
      clickDismissedRef.current = false;
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setIsTooltipVisible(true);
      broadcastTooltipShow(instanceIdRef.current);
    }
  }, [isTablet, isMobile]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Dismiss tooltip on outside tap (tablet/mobile only)
  useEffect(() => {
    if (!isTooltipVisible || (!isTablet && !isMobile)) return;
    const handleOutsideClick = (e: PointerEvent) => {
      // Ignore taps on the icon itself (handleIconClick handles that)
      if (iconRef.current && iconRef.current.contains(e.target as Node)) return;
      clickLockedRef.current = false;
      setIsTooltipVisible(false);
    };
    document.addEventListener('pointerdown', handleOutsideClick);
    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [isTooltipVisible, isTablet, isMobile]);

  // Tablet touch-friendly styling
  if (isTablet) {
    return (
      <>
        <label
          htmlFor={id}
          className="flex items-center cursor-pointer w-full shrink-0"
          style={{
            minHeight: '60px',
            gap: '16px',
            borderRadius: '4px',
            border: `1px solid ${checked ? 'var(--color-primary-brand)' : '#D2D2D2'}`,
            padding: '16px',
          }}
        >
          <input
            type="radio"
            id={id}
            name={name}
            checked={checked}
            onChange={onChange}
            style={{ position: 'fixed', opacity: 0, width: 1, height: 1 }}
          />
          {/* Custom radio circle */}
          <div
            className="flex items-center justify-center shrink-0"
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: `1px solid ${checked ? 'var(--color-primary-brand)' : '#D2D2D2'}`,
            }}
          >
            {checked && (
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--color-primary-brand)',
                }}
              />
            )}
          </div>
          {/* Label and optional info icon */}
          <span className="flex items-center flex-1 min-w-0" style={{ gap: '4px' }}>
            <span className={cn(useMediumText ? typography.bodyMedium : typography.body, 'text-black', 'flex-1 min-w-0')}>
              {label}
            </span>
            {showInfoIcon && (
              <span
                ref={iconRef}
                onMouseEnter={handleIconMouseEnter}
                onMouseLeave={handleIconMouseLeave}
                onClick={handleIconClick}
                className="shrink-0 inline-flex"
              >
                <QuizInfoIcon
                  style={{ width: '16px', height: '16px' }}
                  aria-hidden="true"
                />
              </span>
            )}
          </span>
        </label>
        {isTooltipVisible && tooltipData && (
          <QuizThemeTooltipPortal
            tooltip={tooltipData}
            triggerRef={iconRef}
            isMobile={isMobile}
            isTablet={isTablet && !isMobile}
          />
        )}
      </>
    );
  }

  return (
    <>
      <label
        htmlFor={id}
        className="flex items-center cursor-pointer"
        style={{ gap: '8px' }}
      >
        <input
          type="radio"
          id={id}
          name={name}
          checked={checked}
          onChange={onChange}
          style={{ position: 'fixed', opacity: 0, width: 1, height: 1 }}
        />
        {/* Custom radio circle */}
        <div
          className="flex items-center justify-center shrink-0"
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: `1px solid ${checked ? 'var(--color-primary-brand)' : '#D2D2D2'}`,
          }}
        >
          {checked && (
            <div
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-primary-brand)',
              }}
            />
          )}
        </div>
        {/* Label and optional info icon */}
        <span className="flex items-center" style={{ gap: '4px' }}>
          <span className={cn(useMediumText ? typography.bodyMedium : typography.body, 'text-black')}>
            {label}
          </span>
          {showInfoIcon && (
            <span
              ref={iconRef}
              onMouseEnter={handleIconMouseEnter}
              onMouseLeave={handleIconMouseLeave}
              onClick={handleIconClick}
              className="shrink-0 inline-flex"
            >
              <QuizInfoIcon
                style={{ width: '16px', height: '16px' }}
                aria-hidden="true"
              />
            </span>
          )}
        </span>
      </label>
      {isTooltipVisible && tooltipData && (
        <QuizThemeTooltipPortal
          tooltip={tooltipData}
          triggerRef={iconRef}
          isMobile={isMobile}
        />
      )}
    </>
  );
}

/**
 * Custom Checkbox component for the quiz (Step 4)
 */
interface QuizCheckboxProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: () => void;
  /** Whether to use tablet touch-friendly styling */
  isTablet?: boolean;
}

function QuizCheckbox({ id, label, checked, onChange, isTablet }: QuizCheckboxProps) {
  // Tablet touch-friendly styling
  if (isTablet) {
    return (
      <label
        htmlFor={id}
        className="flex items-center cursor-pointer w-full shrink-0"
        style={{
          minHeight: '60px',
          gap: '16px',
          borderRadius: '4px',
          border: `1px solid ${checked ? 'var(--color-primary-brand)' : '#D2D2D2'}`,
          padding: '16px',
        }}
      >
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={onChange}
          style={{ position: 'fixed', opacity: 0, width: 1, height: 1 }}
        />
        {/* Checkbox icon - 20x20px same as ResourceCatalog */}
        {checked ? (
          <CheckboxCheckedIcon
            className="shrink-0"
            style={{ width: '20px', height: '20px' }}
            aria-hidden="true"
          />
        ) : (
          <CheckboxUncheckedIcon
            className="shrink-0"
            style={{ width: '20px', height: '20px' }}
            aria-hidden="true"
          />
        )}
        <span className={cn(typography.bodyMedium, 'text-black', 'flex-1 min-w-0')}>
          {label}
        </span>
      </label>
    );
  }

  return (
    <label
      htmlFor={id}
      className="flex items-center cursor-pointer"
      style={{ gap: '8px' }}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        style={{ position: 'fixed', opacity: 0, width: 1, height: 1 }}
      />
      {/* Checkbox icon - 20x20px same as ResourceCatalog */}
      {checked ? (
        <CheckboxCheckedIcon
          className="shrink-0"
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
      ) : (
        <CheckboxUncheckedIcon
          className="shrink-0"
          style={{ width: '20px', height: '20px' }}
          aria-hidden="true"
        />
      )}
      <span className={cn(typography.bodyMedium, 'text-black')}>
        {label}
      </span>
    </label>
  );
}

/**
 * Quiz Result Resource Card - Individual resource card for Step 5
 */
interface QuizResultResourceCardProps {
  resource: QuizResourceData;
  saveLabel: string;
  savedLabel: string;
  onSave: (resource: QuizResourceData) => void;
  isSaved: boolean;
  /** Whether to use mobile styling (full width) */
  isMobile?: boolean;
  /** Whether to use tablet styling */
  isTablet?: boolean;
}

function QuizResultResourceCard({ resource, saveLabel, savedLabel, onSave, isSaved, isMobile, isTablet }: QuizResultResourceCardProps) {
  const handleLaunch = () => {
    if (resource.href) {
      window.open(resource.href, '_blank', 'noopener,noreferrer');
    }
  };

  // Desktop uses small size (32px), mobile/tablet uses default size (48px)
  const buttonSize = (isMobile || isTablet) ? 'default' : 'small';
  // Icon size matches button size (smaller for small button)
  const iconClass = (isMobile || isTablet) ? 'w-5 h-6' : 'w-4 h-5';

  return (
    <div
      className={cn('flex flex-col bg-white', isMobile ? 'w-full' : 'flex-1')}
      style={{
        maxWidth: isMobile ? '100%' : '309px',
        minHeight: '164px',
        borderRadius: '4px',
        border: '1px solid #EBEBEB',
        padding: '12px',
        boxShadow: '0px 4px 4px 0px #BBBBBB40',
      }}
    >
      {/* Top row: Type badge and launch icon */}
      <div className="flex justify-between items-start">
        <span
          style={{
            color: '#0098d3',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontFamily: "'Source Sans Pro', sans-serif",
          }}
        >
          {resource.type}
        </span>
        <button
          onClick={handleLaunch}
          className="flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer"
          aria-label={`Open ${resource.title} in new tab`}
        >
          <GoToExternalSiteIcon className="w-4 h-4" style={{ color: '#0A4D8F' }} aria-hidden="true" />
        </button>
      </div>

      {/* Title - up to 2 lines with ellipsis */}
      <p
        className={cn(typography.buttonDefault, 'text-black m-0')}
        style={{
          marginTop: '4px',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {resource.title}
      </p>

      {/* Description wrapper - flex-1 to push button to bottom */}
      <div className="flex-1" style={{ marginTop: '4px' }}>
        {/* Description - up to 2 lines with ellipsis */}
        <p
          className={cn(typography.body, 'text-black m-0')}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {resource.description}
        </p>
      </div>

      {/* Save resource button - at bottom of card */}
      <Button
        variant={isSaved ? 'primary' : 'secondary'}
        size={buttonSize}
        onClick={() => onSave(resource)}
        className="w-full"
        style={{ marginTop: '12px' }}
        aria-label={isSaved ? `${resource.title} resource saved` : `Save resource ${resource.title}`}
      >
        <span>{isSaved ? savedLabel : saveLabel}</span>
        <BookmarkIcon className={iconClass} aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * Quiz Result Accordion Card - Gray container showing accordion results
 */
interface QuizResultAccordionCardProps {
  topic: QuizTopicData;
  themeId: string;
  viewAllLabel: string;
  saveLabel: string;
  savedLabel: string;
  onSaveResource: (resource: QuizResourceData) => void;
  savedResourceIds: string[];
  /** Whether to use mobile layout (stacked resources) */
  isMobile?: boolean;
  /** Whether to use tablet styling */
  isTablet?: boolean;
}

function QuizResultAccordionCard({
  topic,
  themeId,
  viewAllLabel,
  saveLabel,
  savedLabel,
  onSaveResource,
  savedResourceIds,
  isMobile,
  isTablet,
}: QuizResultAccordionCardProps) {
  const handleViewAll = () => {
    // Navigate to theme page with accordion opened
    window.location.href = `/find-information?category=${themeId}&accordion=${topic.id}`;
  };

  // Get first 2 resources
  const displayResources = topic.resources?.slice(0, 2) || [];

  return (
    <div
      className="flex flex-col w-full"
      style={{
        maxWidth: isMobile ? '100%' : '662px',
        minHeight: isMobile ? undefined : '236px',
        gap: '12px',
        borderRadius: '4px',
        padding: '16px',
        backgroundColor: '#F2F2F2',
      }}
    >
      {/* Header row: Accordion title and View all link */}
      <div className={cn('flex', isMobile ? 'flex-col items-start gap-2' : 'items-center justify-between')}>
        <p className={cn(typography.subtitle, 'text-black m-0')}>
          {topic.title}
        </p>
        <button
          onClick={handleViewAll}
          className={cn(
            typography.buttonDefault,
            'flex items-center text-[#0A4D8F] hover:opacity-70 transition-opacity cursor-pointer bg-transparent border-none'
          )}
          style={{ gap: '8px' }}
        >
          <span>{viewAllLabel}</span>
          <IconRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>

      {/* Resource cards - side by side on desktop/tablet, stacked on mobile */}
      <div className={cn('flex flex-wrap', isMobile ? 'flex-col' : 'flex-row')} style={{ gap: '12px' }}>
        {displayResources.map((resource) => (
          <QuizResultResourceCard
            key={resource.id}
            resource={resource}
            saveLabel={saveLabel}
            savedLabel={savedLabel}
            onSave={onSaveResource}
            isSaved={savedResourceIds.includes(resource.id)}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Service Result Banner - Shows a link to an external service tool
 * Used for the services path results (Quality Care Navigator, Healthy Aging Services Directory)
 */
interface ServiceResultBannerProps {
  title: string;
  description: string;
  buttonLabel: string;
  href: string;
  /** Whether to use mobile styling */
  isMobile?: boolean;
}

function ServiceResultBanner({ title, description, buttonLabel, href, isMobile }: ServiceResultBannerProps) {
  const isInternal = href.startsWith('/');
  const handleClick = () => {
    if (isInternal) {
      // Same-tab navigation preserves sessionStorage (catalog items)
      window.location.href = href;
    } else {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  // Mobile layout: stacked vertically
  if (isMobile) {
    return (
      <div
        className="flex flex-col bg-white"
        style={{
          width: '100%',
          borderRadius: '4px',
          border: '1px solid #EBEBEB',
          padding: '16px',
          boxShadow: '0px 4px 4px 0px #BBBBBB40',
        }}
      >
        <p className={cn(typography.headingLarge, 'text-black m-0')}>
          {title}
        </p>
        <p
          className={cn(typography.body, 'text-black m-0')}
          style={{ marginTop: '4px' }}
        >
          {description}
        </p>
        <Button
          variant="secondary"
          size="medium"
          onClick={handleClick}
          className="w-full"
          style={{ marginTop: '12px' }}
        >
          <span>{buttonLabel}</span>
          <IconRight className="w-4 h-4" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  // Desktop/Tablet layout: text on left (with 24px gap from button), button on right
  return (
    <div
      className="flex bg-white"
      style={{
        width: '662px',
        borderRadius: '4px',
        border: '1px solid #EBEBEB',
        padding: '16px',
        boxShadow: '0px 4px 4px 0px #BBBBBB40',
        gap: '24px',
      }}
    >
      {/* Left side: Title and description */}
      <div className="flex flex-col flex-1">
        <p className={cn(typography.headingLarge, 'text-black m-0')}>
          {title}
        </p>
        <p
          className={cn(typography.body, 'text-black m-0')}
          style={{ marginTop: '4px' }}
        >
          {description}
        </p>
      </div>

      {/* Right side: Button (aligned to top) */}
      <Button
        variant="secondary"
        size="medium"
        onClick={handleClick}
        className="shrink-0 self-start"
      >
        <span>{buttonLabel}</span>
        <IconRight className="w-4 h-4" aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * ResourceFinderQuizModal - Modal for the Resource Finder Quiz
 *
 * Displays a modal with the Resource Finder Quiz interface.
 *
 * Layout specifications:
 * - Width: 742px
 * - Height: 600px
 * - Padding: 40px top, 40px right, 24px bottom, 40px left
 */
export function ResourceFinderQuizModal({
  content,
  themes,
  isOpen,
  onClose,
  onCatalogChange,
  initialResourceType,
  className,
}: ResourceFinderQuizModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [resourceType, setResourceType] = useState<'informational' | 'services' | 'events'>(
    initialResourceType || 'informational'
  );
  const [userType, setUserType] = useState<'older-adult' | 'caregiver'>('older-adult');
  // Service type for services path
  const [serviceType, setServiceType] = useState<'facility-based' | 'healthy-aging'>('facility-based');
  // Event type for events path
  const [eventType, setEventType] = useState<'virtual' | 'near-me'>('virtual');
  const [topic, setTopic] = useState<string>('');
  const [selectedSubthemes, setSelectedSubthemes] = useState<string[]>([]);
  const [savedResourceIds, setSavedResourceIds] = useState<string[]>([]);
  // Track if user saved any resources during this quiz session
  const [hadSavedResources, setHadSavedResources] = useState(false);
  // Track tablet breakpoint (below 1200px desktop breakpoint)
  const [isTablet, setIsTablet] = useState(false);
  // Track mobile breakpoint (below 800px)
  const [isMobile, setIsMobile] = useState(false);

  // Detect tablet breakpoint
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1199px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsTablet(e.matches);
    };
    // Set initial value
    handleChange(mediaQuery);
    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Detect mobile breakpoint
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 799px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };
    // Set initial value
    handleChange(mediaQuery);
    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Sync resource type from banner selection when modal opens
  useEffect(() => {
    if (isOpen && initialResourceType) {
      setResourceType(initialResourceType);
    }
  }, [isOpen, initialResourceType]);

  // Sync saved resource IDs with catalog on mount/update
  useEffect(() => {
    if (isOpen && currentStep === 5) {
      // Check which resources are already in catalog
      const selectedTheme = themes?.find((t) => t.id === topic);
      const allResourceIds = selectedTheme?.topics
        ?.filter((t) => selectedSubthemes.includes(t.id))
        ?.flatMap((t) => t.resources?.map((r) => r.id) || []) || [];

      const saved = allResourceIds.filter((id) => isInCatalog(id));
      setSavedResourceIds(saved);
    }
  }, [isOpen, currentStep, topic, selectedSubthemes, themes]);

  if (!content) {
    return null;
  }

  // Generate step 3 options from themes based on user type
  const step3Options = themes?.map((theme) => ({
    id: `topic-${theme.id}`,
    label: userType === 'older-adult' ? theme.olderAdultThemeQuizOption : theme.caregiverThemeQuizOption,
    value: theme.id,
    tooltipData: theme.topics?.length ? {
      heading: 'This theme includes:',
      items: theme.topics.map((t) => t.subthemeQuizOption),
    } : undefined,
  })) || [];

  // Generate step 4 options from selected theme's topics
  const selectedTheme = themes?.find((t) => t.id === topic);
  const step4Options = selectedTheme?.topics?.map((topicItem) => ({
    id: `subtheme-${topicItem.id}`,
    label: topicItem.subthemeQuizOption,
    value: topicItem.id,
  })) || [];

  // Toggle subtheme selection
  const handleSubthemeToggle = (subthemeId: string) => {
    setSelectedSubthemes((prev) =>
      prev.includes(subthemeId)
        ? prev.filter((id) => id !== subthemeId)
        : [...prev, subthemeId]
    );
  };

  // Toggle select all / deselect all subthemes
  const handleSelectAllToggle = () => {
    const allSubthemeIds = step4Options.map((option) => option.value);
    const allSelected = allSubthemeIds.length > 0 && allSubthemeIds.every((id) => selectedSubthemes.includes(id));
    if (allSelected) {
      setSelectedSubthemes([]);
    } else {
      setSelectedSubthemes(allSubthemeIds);
    }
  };

  const handleClose = () => {
    // Capture whether resources were saved before resetting
    const didSaveResources = hadSavedResources;
    // Reset state when closing
    setCurrentStep(1);
    setResourceType('informational');
    setUserType('older-adult');
    setServiceType('facility-based');
    setEventType('virtual');
    setTopic('');
    setSelectedSubthemes([]);
    setHadSavedResources(false);
    onClose(didSaveResources);
  };

  // Handle topic selection - clear subthemes when topic changes
  const handleTopicChange = (newTopic: string) => {
    setTopic(newTopic);
    setSelectedSubthemes([]);
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      // For services or events path, skip from step 2 directly to step 5
      if (currentStep === 2 && (resourceType === 'services' || resourceType === 'events')) {
        setCurrentStep(5);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      // For services or events path, go from step 5 back to step 2
      if (currentStep === 5 && (resourceType === 'services' || resourceType === 'events')) {
        setCurrentStep(2);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleRestart = () => {
    setCurrentStep(1);
    setResourceType('informational');
    setUserType('older-adult');
    setServiceType('facility-based');
    setEventType('virtual');
    setTopic('');
    setSelectedSubthemes([]);
    setSavedResourceIds([]);
  };

  const handleComplete = () => {
    handleClose();
  };

  const handleSaveResource = (resource: QuizResourceData) => {
    // Check if already saved - toggle behavior
    if (savedResourceIds.includes(resource.id)) {
      // Remove from catalog
      removeCatalogItem(resource.id);
      setSavedResourceIds((prev) => prev.filter((id) => id !== resource.id));
      onCatalogChange?.();
    } else {
      // Add to catalog
      const catalogItem: CatalogStorageItem = {
        id: resource.id,
        type: resource.type,
        title: resource.title,
        source: resource.source,
        description: resource.description,
        href: resource.href,
        sourceType: 'find-information',
        actions: {
          canShare: true,
          canPrint: true,
          canSave: true,
        },
      };

      const added = addCatalogItem(catalogItem);
      if (added) {
        setSavedResourceIds((prev) => [...prev, resource.id]);
        setHadSavedResources(true);
        onCatalogChange?.();
      }
    }
  };

  // Get selected topics for Step 5 (reuse selectedTheme from step4Options)
  const selectedTopics = selectedTheme?.topics?.filter((t) =>
    selectedSubthemes.includes(t.id)
  ) || [];

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 bg-black/50 z-[130]"
          data-testid="quiz-modal-overlay"
          onClick={handleClose}
        />
        <Dialog.Content
          className={cn(
            'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
            'z-[131]',
            'focus:outline-none',
            'bg-white rounded-[4px]',
            'flex flex-col',
            className
          )}
          style={{
            width: isMobile ? 'calc(100vw - 32px)' : '742px',
            maxWidth: isMobile ? '396px' : undefined,
            height: isMobile ? '852px' : isTablet ? '900px' : '600px',
            maxHeight: (isMobile || isTablet) ? 'calc(100vh - 48px)' : undefined,
            paddingTop: isMobile ? '16px' : '40px',
            paddingRight: isMobile ? '16px' : '40px',
            paddingBottom: '24px',
            paddingLeft: isMobile ? '16px' : '40px',
            boxShadow: '0px 4px 4px 0px rgba(187, 187, 187, 0.25)',
            overflow: 'clip',
          }}
          aria-describedby={undefined}
          data-testid="quiz-modal-content"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Visually hidden title for accessibility */}
          <VisuallyHidden.Root asChild>
            <Dialog.Title>{content.title}</Dialog.Title>
          </VisuallyHidden.Root>

          {/* Exit Button - absolute on desktop/tablet */}
          {!isMobile && (
            <Dialog.Close
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer"
              aria-label={content.closeLabel}
              data-testid="quiz-modal-close"
            >
              <XBoldIcon className="w-5 h-5 text-black" aria-hidden="true" />
            </Dialog.Close>
          )}

          {/* Mobile: Progress bar and exit button on same row */}
          {isMobile && (
            <div className="flex items-center shrink-0" style={{ gap: '12px' }}>
              {/* Progress Indicator - flexible width */}
              <div
                className="flex flex-1"
                style={{ gap: '6px' }}
                role="progressbar"
                aria-valuenow={currentStep}
                aria-valuemin={1}
                aria-valuemax={TOTAL_STEPS}
                aria-label={`Step ${currentStep} of ${TOTAL_STEPS}`}
              >
                {Array.from({ length: TOTAL_STEPS }, (_, index) => (
                  <div
                    key={index}
                    className="flex-1"
                    style={{
                      height: '4px',
                      backgroundColor: index === currentStep - 1 ? 'var(--color-primary-brand)' : '#D2D2D2',
                    }}
                    aria-hidden="true"
                  />
                ))}
              </div>
              {/* Exit button */}
              <Dialog.Close
                className="w-10 h-10 flex items-center justify-center hover:opacity-70 transition-opacity cursor-pointer shrink-0"
                style={{ marginRight: '-8px' }}
                aria-label={content.closeLabel}
                data-testid="quiz-modal-close"
              >
                <XBoldIcon className="w-5 h-5 text-black" aria-hidden="true" />
              </Dialog.Close>
            </div>
          )}

          {/* Progress Indicator - desktop/tablet (5 step rectangles) */}
          {!isMobile && (
            <div
              className="flex shrink-0"
              style={{ width: '547px', gap: '6px' }}
              role="progressbar"
              aria-valuenow={currentStep}
              aria-valuemin={1}
              aria-valuemax={TOTAL_STEPS}
              aria-label={`Step ${currentStep} of ${TOTAL_STEPS}`}
            >
              {Array.from({ length: TOTAL_STEPS }, (_, index) => (
                <div
                  key={index}
                  className="flex-1"
                  style={{
                    height: '4px',
                    backgroundColor: index === currentStep - 1 ? 'var(--color-primary-brand)' : '#D2D2D2',
                  }}
                  aria-hidden="true"
                />
              ))}
            </div>
          )}

          {/* Step 1 Content */}
          {currentStep === 1 && content.step1 && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Title */}
              <h2 className={cn(typography.headingMedium, 'text-black m-0 shrink-0')}>
                {content.step1.title}
              </h2>

              {/* Subtitle - 6px gap */}
              <p className={cn(typography.body, 'text-black m-0 shrink-0')} style={{ marginTop: '6px' }}>
                {content.step1.subtitle}
              </p>

              {/* Question - 24px gap */}
              <p className={cn(typography.subtitle, 'text-black m-0 shrink-0')} style={{ marginTop: '24px' }}>
                {content.step1.question}
              </p>

              {/* Radio options - scrollable on mobile/tablet */}
              <div
                className={cn('flex flex-col', (isMobile || isTablet) && 'flex-1 min-h-0 overflow-y-auto')}
                style={{ marginTop: (isMobile || isTablet) ? '18px' : '8px', gap: (isMobile || isTablet) ? '24px' : '8px' }}
                role="radiogroup"
                aria-label={content.step1.question}
              >
                {content.step1.options.map((option) => (
                  <QuizRadioButton
                    key={option.id}
                    id={option.id}
                    name="resource-type"
                    label={option.label}
                    checked={resourceType === option.value}
                    onChange={() => setResourceType(option.value)}
                    useMediumText
                    showInfoIcon={!!option.tooltip}
                    tooltipData={option.tooltip ? { heading: option.tooltip, items: [] } : undefined}
                    isTablet={isMobile || isTablet}
                    isMobile={isMobile}
                  />
                ))}
              </div>

              {/* Spacer to push button to bottom (desktop only) */}
              {!(isMobile || isTablet) && <div className="flex-1" />}

              {/* Next button - bottom right, full width on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col' : 'justify-end')} style={{ marginTop: (isMobile || isTablet) ? '24px' : undefined }}>
                <Button
                  variant="primary"
                  size="large"
                  onClick={handleNext}
                  className={isMobile ? 'w-full' : undefined}
                  data-testid="quiz-next-button"
                >
                  {content.buttons?.next || 'Next'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2 Content - Informational Resources Path */}
          {currentStep === 2 && resourceType === 'informational' && content.step2 && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Question - subtitle typography */}
              <p className={cn(typography.subtitle, 'text-black m-0 shrink-0')}>
                {content.step2.question}
              </p>

              {/* Radio options - scrollable on mobile/tablet */}
              <div
                className={cn('flex flex-col', (isMobile || isTablet) && 'flex-1 min-h-0 overflow-y-auto')}
                style={{ marginTop: '18px', gap: (isMobile || isTablet) ? '24px' : '8px' }}
                role="radiogroup"
                aria-label={content.step2.question}
              >
                {content.step2.options.map((option) => (
                  <QuizRadioButton
                    key={option.id}
                    id={option.id}
                    name="user-type"
                    label={option.label}
                    checked={userType === option.value}
                    onChange={() => setUserType(option.value)}
                    useMediumText
                    isTablet={isMobile || isTablet}
                  />
                ))}
              </div>

              {/* Spacer to push buttons to bottom (desktop only) */}
              {!(isMobile || isTablet) && <div className="flex-1" />}

              {/* Navigation buttons - stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'justify-between')} style={{ marginTop: (isMobile || isTablet) ? '24px' : undefined }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      className="w-full"
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 2 Content - Services Path */}
          {currentStep === 2 && resourceType === 'services' && content.step2Services && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Question - subtitle typography */}
              <p className={cn(typography.subtitle, 'text-black m-0 shrink-0')}>
                {content.step2Services.question}
              </p>

              {/* Radio options - scrollable on mobile/tablet */}
              <div
                className={cn('flex flex-col', (isMobile || isTablet) && 'flex-1 min-h-0 overflow-y-auto')}
                style={{ marginTop: '18px', gap: (isMobile || isTablet) ? '24px' : '8px' }}
                role="radiogroup"
                aria-label={content.step2Services.question}
              >
                {content.step2Services.options.map((option) => (
                  <QuizRadioButton
                    key={option.id}
                    id={option.id}
                    name="service-type"
                    label={option.label}
                    checked={serviceType === option.value}
                    onChange={() => setServiceType(option.value)}
                    useMediumText
                    isTablet={isMobile || isTablet}
                  />
                ))}
              </div>

              {/* Spacer to push buttons to bottom (desktop only) */}
              {!(isMobile || isTablet) && <div className="flex-1" />}

              {/* Navigation buttons - stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'justify-between')} style={{ marginTop: (isMobile || isTablet) ? '24px' : undefined }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      className="w-full"
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 2 Content - Events Path */}
          {currentStep === 2 && resourceType === 'events' && content.step2Events && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Question - subtitle typography */}
              <p className={cn(typography.subtitle, 'text-black m-0 shrink-0')}>
                {content.step2Events.question}
              </p>

              {/* Radio options - scrollable on mobile/tablet */}
              <div
                className={cn('flex flex-col', (isMobile || isTablet) && 'flex-1 min-h-0 overflow-y-auto')}
                style={{ marginTop: '18px', gap: (isMobile || isTablet) ? '24px' : '8px' }}
                role="radiogroup"
                aria-label={content.step2Events.question}
              >
                {content.step2Events.options.map((option) => (
                  <QuizRadioButton
                    key={option.id}
                    id={option.id}
                    name="event-type"
                    label={option.label}
                    checked={eventType === option.value}
                    onChange={() => setEventType(option.value)}
                    useMediumText
                    isTablet={isMobile || isTablet}
                  />
                ))}
              </div>

              {/* Spacer to push buttons to bottom (desktop only) */}
              {!(isMobile || isTablet) && <div className="flex-1" />}

              {/* Navigation buttons - stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'justify-between')} style={{ marginTop: (isMobile || isTablet) ? '24px' : undefined }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      className="w-full"
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 3 Content */}
          {currentStep === 3 && content.step3 && step3Options.length > 0 && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Question - subtitle typography */}
              <p className={cn(typography.subtitle, 'text-black m-0 shrink-0')}>
                {content.step3.question}
              </p>

              {/* Radio options - scrollable on mobile/tablet */}
              <div
                className={cn('flex flex-col', (isMobile || isTablet) && 'flex-1 min-h-0 overflow-y-auto')}
                style={{ marginTop: '18px', gap: (isMobile || isTablet) ? '24px' : '8px' }}
                role="radiogroup"
                aria-label={content.step3.question}
              >
                {step3Options.map((option) => (
                  <QuizRadioButton
                    key={option.id}
                    id={option.id}
                    name="topic"
                    label={option.label}
                    checked={topic === option.value}
                    onChange={() => handleTopicChange(option.value)}
                    useMediumText
                    showInfoIcon
                    tooltipData={option.tooltipData}
                    isTablet={isMobile || isTablet}
                    isMobile={isMobile}
                  />
                ))}
              </div>

              {/* Spacer to push buttons to bottom (desktop only) */}
              {!(isMobile || isTablet) && <div className="flex-1" />}

              {/* Navigation buttons - stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'justify-between')} style={{ marginTop: (isMobile || isTablet) ? '24px' : undefined }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      disabled={!topic}
                      className="w-full"
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      disabled={!topic}
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 4 Content - Multi-select subthemes */}
          {currentStep === 4 && content.step4 && step4Options.length > 0 && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Question - subtitle typography */}
              <p className={cn(typography.subtitle, 'text-black m-0 shrink-0')}>
                {content.step4.question}
              </p>

              {/* Select all that apply text link */}
              {content.step4.selectAllLabel && (
                <div className="shrink-0" style={{ marginTop: '12px' }}>
                  <button
                    type="button"
                    onClick={handleSelectAllToggle}
                    data-testid="quiz-select-all-button"
                    className="cursor-pointer bg-transparent border-none p-0 hover:underline"
                    style={{
                      color: 'var(--color-primary-brand)',
                      fontFamily: "'Source Sans Pro', sans-serif",
                      fontSize: '18px',
                      fontWeight: 600,
                    }}
                  >
                    {content.step4.selectAllLabel}
                  </button>
                </div>
              )}

              {/* Checkbox options - scrollable on mobile/tablet */}
              <div
                className={cn('flex flex-col', (isMobile || isTablet) && 'flex-1 min-h-0 overflow-y-auto')}
                style={{ marginTop: '18px', gap: (isMobile || isTablet) ? '24px' : '8px' }}
                role="group"
                aria-label={content.step4.question}
              >
                {step4Options.map((option) => (
                  <QuizCheckbox
                    key={option.id}
                    id={option.id}
                    label={option.label}
                    checked={selectedSubthemes.includes(option.value)}
                    onChange={() => handleSubthemeToggle(option.value)}
                    isTablet={isMobile || isTablet}
                  />
                ))}
              </div>

              {/* Spacer to push buttons to bottom (desktop only) */}
              {!(isMobile || isTablet) && <div className="flex-1" />}

              {/* Navigation buttons - stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'justify-between')} style={{ marginTop: (isMobile || isTablet) ? '24px' : undefined }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      disabled={selectedSubthemes.length === 0}
                      className="w-full"
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleNext}
                      disabled={selectedSubthemes.length === 0}
                      data-testid="quiz-next-button"
                    >
                      {content.buttons?.next || 'Next'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 5 Content - Services Path Results */}
          {currentStep === 5 && resourceType === 'services' && content.step5 && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Title - buttonDefault typography */}
              <p className={cn(typography.buttonDefault, 'text-black m-0 shrink-0')}>
                {content.step5.title}
              </p>

              {/* Scrollable results area */}
              <div
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
                style={{ marginTop: '12px' }}
              >
                <div className="flex flex-col" style={{ gap: '12px' }}>
                  {/* Service Result Banner based on service type selection */}
                  {serviceType === 'facility-based' && content.step5.servicesBanners?.facilityBased && (
                    <ServiceResultBanner
                      title={content.step5.servicesBanners.facilityBased.title}
                      description={content.step5.servicesBanners.facilityBased.description}
                      buttonLabel={content.step5.servicesBanners.facilityBased.buttonLabel}
                      href={content.step5.servicesBanners.facilityBased.href}
                      isMobile={isMobile}
                    />
                  )}
                  {serviceType === 'healthy-aging' && content.step5.servicesBanners?.healthyAging && (
                    <ServiceResultBanner
                      title={content.step5.servicesBanners.healthyAging.title}
                      description={content.step5.servicesBanners.healthyAging.description}
                      buttonLabel={content.step5.servicesBanners.healthyAging.buttonLabel}
                      href={content.step5.servicesBanners.healthyAging.href}
                      isMobile={isMobile}
                    />
                  )}

                  {/* Feedback Submission Banner - 32px gap from service result banner (12px gap + 20px margin) */}
                  {content.step5?.feedbackBanner && (
                    <div
                      className={cn('flex', isMobile ? 'flex-col gap-3' : 'items-center justify-between')}
                      style={{
                        width: isMobile ? '100%' : '662px',
                        minHeight: isMobile ? undefined : '76px',
                        borderRadius: '4px',
                        padding: '12px',
                        backgroundColor: '#D2EDEE',
                        marginTop: '20px',
                      }}
                    >
                      {/* Text content */}
                      <div className="flex flex-col" style={{ gap: '4px' }}>
                        <p className={cn(typography.buttonDefault, 'text-black m-0')}>
                          {content.step5.feedbackBanner.title}
                        </p>
                        <p className={cn(typography.body, 'text-black m-0')}>
                          {content.step5.feedbackBanner.subtitle}
                        </p>
                      </div>

                      {/* Button */}
                      <Button
                        variant="secondary"
                        size="medium"
                        className={isMobile ? 'w-full' : undefined}
                        onClick={() => window.open('https://www.surveymonkey.com/r/FeedbackForResourceHub', '_blank', 'noopener,noreferrer')}
                      >
                        <span>{content.step5.feedbackBanner.buttonLabel}</span>
                        <IconRight className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation buttons - fixed at bottom, stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'items-center')} style={{ marginTop: '16px', gap: isMobile ? undefined : '16px' }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleComplete}
                      className="w-full"
                      data-testid="quiz-complete-button"
                    >
                      {content.step5.completeLabel}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleRestart}
                      className="w-full"
                      data-testid="quiz-restart-button"
                    >
                      {content.step5.restartLabel}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleRestart}
                      data-testid="quiz-restart-button"
                    >
                      {content.step5.restartLabel}
                    </Button>
                    <div className="flex-1" />
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleComplete}
                      data-testid="quiz-complete-button"
                    >
                      {content.step5.completeLabel}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 5 Content - Events Path Results */}
          {currentStep === 5 && resourceType === 'events' && content.step5 && (
            <div className="flex flex-col flex-1 min-h-0" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Title - buttonDefault typography */}
              <p className={cn(typography.buttonDefault, 'text-black m-0 shrink-0')}>
                {content.step5.title}
              </p>

              {/* Scrollable results area */}
              <div
                className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
                style={{ marginTop: '12px' }}
              >
                <div className="flex flex-col" style={{ gap: '12px' }}>
                  {/* Event Result Banner based on event type selection */}
                  {eventType === 'virtual' && content.step5.eventsBanners?.virtual && (
                    <ServiceResultBanner
                      title={content.step5.eventsBanners.virtual.title}
                      description={content.step5.eventsBanners.virtual.description}
                      buttonLabel={content.step5.eventsBanners.virtual.buttonLabel}
                      href={content.step5.eventsBanners.virtual.href}
                      isMobile={isMobile}
                    />
                  )}
                  {/* Near Me Events - Title and list of links */}
                  {eventType === 'near-me' && content.step5.eventsBanners?.nearMe && (
                    <div className="flex flex-col">
                      {/* Title - subtitle typography */}
                      <p className={cn(typography.subtitle, 'text-black m-0')}>
                        {content.step5.eventsBanners.nearMe.title}
                      </p>

                      {/* Links list - 12px gap from title, 12px between links */}
                      <div className="flex flex-col" style={{ marginTop: '12px', gap: '12px' }}>
                        {content.step5.eventsBanners.nearMe.links.map((link, index) => {
                          const isLinkInternal = link.href.startsWith('/');
                          return (
                            <a
                              key={index}
                              href={link.href}
                              {...(!isLinkInternal && { target: '_blank', rel: 'noopener noreferrer' })}
                              onClick={isLinkInternal ? (e) => { e.preventDefault(); window.location.href = link.href; } : undefined}
                              className={cn(
                                typography.buttonLarge,
                                'flex items-center no-underline hover:opacity-70 transition-opacity'
                              )}
                              style={{ color: 'var(--color-primary-brand)', gap: '8px' }}
                            >
                              <span>{link.label}</span>
                              <IconRight style={{ width: '22px', height: '22px' }} aria-hidden="true" />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Feedback Submission Banner - 32px gap from results (12px gap + 20px margin) */}
                  {content.step5?.feedbackBanner && (
                    <div
                      className={cn('flex', isMobile ? 'flex-col gap-3' : 'items-center justify-between')}
                      style={{
                        width: isMobile ? '100%' : '662px',
                        minHeight: isMobile ? undefined : '76px',
                        borderRadius: '4px',
                        padding: '12px',
                        backgroundColor: '#D2EDEE',
                        marginTop: '20px',
                      }}
                    >
                      {/* Text content */}
                      <div className="flex flex-col" style={{ gap: '4px' }}>
                        <p className={cn(typography.buttonDefault, 'text-black m-0')}>
                          {content.step5.feedbackBanner.title}
                        </p>
                        <p className={cn(typography.body, 'text-black m-0')}>
                          {content.step5.feedbackBanner.subtitle}
                        </p>
                      </div>

                      {/* Button */}
                      <Button
                        variant="secondary"
                        size="medium"
                        className={isMobile ? 'w-full' : undefined}
                        onClick={() => window.open('https://www.surveymonkey.com/r/FeedbackForResourceHub', '_blank', 'noopener,noreferrer')}
                      >
                        <span>{content.step5.feedbackBanner.buttonLabel}</span>
                        <IconRight className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation buttons - fixed at bottom, stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'items-center')} style={{ marginTop: '16px', gap: isMobile ? undefined : '16px' }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleComplete}
                      className="w-full"
                      data-testid="quiz-complete-button"
                    >
                      {content.step5.completeLabel}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleRestart}
                      className="w-full"
                      data-testid="quiz-restart-button"
                    >
                      {content.step5.restartLabel}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleRestart}
                      data-testid="quiz-restart-button"
                    >
                      {content.step5.restartLabel}
                    </Button>
                    <div className="flex-1" />
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleComplete}
                      data-testid="quiz-complete-button"
                    >
                      {content.step5.completeLabel}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 5 Content - Informational Resources Path Results */}
          {currentStep === 5 && resourceType === 'informational' && content.step5 && selectedTopics.length > 0 && (
            <div className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden" style={{ marginTop: isMobile ? '24px' : '44px' }}>
              {/* Title - buttonDefault typography */}
              <p className={cn(typography.buttonDefault, 'text-black m-0 shrink-0')}>
                {content.step5.title}
              </p>

              {/* Scrollable results area - only cards scroll */}
              <div
                className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden"
                style={{ marginTop: '12px' }}
              >
                <div className="flex flex-col" style={{ gap: '12px', maxWidth: '100%' }}>
                  {selectedTopics.map((topicItem) => (
                    <QuizResultAccordionCard
                      key={topicItem.id}
                      topic={topicItem}
                      themeId={topic}
                      viewAllLabel={content.step5?.viewAllLabel || 'View all resources'}
                      saveLabel={content.step5?.saveToCatalogLabel || 'Save resource'}
                      savedLabel={content.step5?.savedToCatalogLabel || 'Resource saved'}
                      onSaveResource={handleSaveResource}
                      savedResourceIds={savedResourceIds}
                      isMobile={isMobile}
                      isTablet={isTablet}
                    />
                  ))}

                  {/* Feedback Submission Banner - 32px gap from result cards (12px gap + 20px margin) */}
                  {content.step5?.feedbackBanner && (
                    <div
                      className={cn('flex', isMobile ? 'flex-col gap-3' : 'items-center justify-between')}
                      style={{
                        width: isMobile ? '100%' : '662px',
                        minHeight: isMobile ? undefined : '76px',
                        borderRadius: '4px',
                        padding: '12px',
                        backgroundColor: '#D2EDEE',
                        marginTop: '20px',
                      }}
                    >
                      {/* Text content */}
                      <div className="flex flex-col" style={{ gap: '4px' }}>
                        <p className={cn(typography.buttonDefault, 'text-black m-0')}>
                          {content.step5.feedbackBanner.title}
                        </p>
                        <p className={cn(typography.body, 'text-black m-0')}>
                          {content.step5.feedbackBanner.subtitle}
                        </p>
                      </div>

                      {/* Button */}
                      <Button
                        variant="secondary"
                        size="medium"
                        className={isMobile ? 'w-full' : undefined}
                        onClick={() => window.open('https://www.surveymonkey.com/r/FeedbackForResourceHub', '_blank', 'noopener,noreferrer')}
                      >
                        <span>{content.step5.feedbackBanner.buttonLabel}</span>
                        <IconRight className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation buttons - fixed at bottom, stacked on mobile */}
              <div className={cn('flex shrink-0', isMobile ? 'flex-col gap-3' : 'items-center')} style={{ marginTop: '16px', gap: isMobile ? undefined : '16px' }}>
                {isMobile ? (
                  <>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleComplete}
                      className="w-full"
                      data-testid="quiz-complete-button"
                    >
                      {content.step5.completeLabel}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleRestart}
                      className="w-full"
                      data-testid="quiz-restart-button"
                    >
                      {content.step5.restartLabel}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      className="w-full"
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleBack}
                      data-testid="quiz-back-button"
                    >
                      {content.buttons?.back || 'Back'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="large"
                      onClick={handleRestart}
                      data-testid="quiz-restart-button"
                    >
                      {content.step5.restartLabel}
                    </Button>
                    <div className="flex-1" />
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleComplete}
                      data-testid="quiz-complete-button"
                    >
                      {content.step5.completeLabel}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

ResourceFinderQuizModal.displayName = 'ResourceFinderQuizModal';
